import { useCallback } from 'react';

import {
  FirebaseAuthentication,
  type SignInResult,
} from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import firebase from 'firebase/compat/app';

import {
  appleProvider,
  auth,
  facebookProvider,
  googleProvider,
} from '../shared/firebase/firebase-client';

/**
 * Native Firebase Auth (plugin) does not sign in the Firebase JS SDK by default.
 * Storage / Firestore via the web SDK need `auth.currentUser` or a manual REST token.
 * @see https://github.com/capawesome-team/capacitor-firebase/blob/main/packages/authentication/docs/firebase-js-sdk.md
 */

/**
 * After native email/password sign-in, try to mirror the session in the JS SDK so
 * `auth.currentUser` is set. If the WebView blocks this, uploads still work via
 * Storage REST + `FirebaseAuthentication.getIdToken()` (see `firebase-storage-utils.ts`).
 */
async function syncNativeEmailPasswordToWebFirebase(
  email: string,
  password: string,
): Promise<void> {
  try {
    const cred = firebase.auth.EmailAuthProvider.credential(email, password);
    await auth.signInWithCredential(cred);
  } catch {
    // Best-effort; native session + plugin token remain valid for REST Storage.
  }
}

async function syncNativeSignInToWebFirebase(
  result: SignInResult,
): Promise<void> {
  const { credential, user } = result;
  if (!user || !credential?.providerId) {
    return;
  }

  if (auth.currentUser?.uid === user.uid) {
    return;
  }

  try {
    if (credential.providerId === 'google.com' && credential.idToken) {
      const cred = firebase.auth.GoogleAuthProvider.credential(
        credential.idToken,
        credential.accessToken,
      );
      await auth.signInWithCredential(cred);
      return;
    }

    if (credential.providerId === 'facebook.com' && credential.accessToken) {
      const cred = firebase.auth.FacebookAuthProvider.credential(
        credential.accessToken,
      );
      await auth.signInWithCredential(cred);
      return;
    }

    if (credential.providerId === 'apple.com' && credential.idToken) {
      const cred = appleProvider.credential({
        idToken: credential.idToken,
        rawNonce: credential.nonce,
      });
      await auth.signInWithCredential(cred);
      return;
    }
  } catch {
    // OAuth sync is best-effort; Storage on native can fall back to REST + plugin ID token.
  }
}

export function useFirebaseAuthInterface() {
  const isNative = Capacitor.getPlatform() !== 'web';

  const getCurrentUser = useCallback(async () => {
    const result = await FirebaseAuthentication.getCurrentUser();
    return result.user;
  }, []);

  async function getIdToken() {
    if (isNative) {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        return;
      }

      const result = await FirebaseAuthentication.getIdToken();
      return result.token;
    }

    return auth.currentUser?.getIdToken();
  }

  function createUserWithEmailAndPassword(data: {
    email: string;
    password: string;
  }) {
    // if (isNative) {
    //   return FirebaseAuthentication.createUserWithEmailAndPassword(data);
    // }

    return auth.createUserWithEmailAndPassword(data.email, data.password);
  }

  async function signInWithEmailAndPassword(data: {
    email: string;
    password: string;
  }) {
    if (isNative) {
      const result =
        await FirebaseAuthentication.signInWithEmailAndPassword(data);
      await syncNativeEmailPasswordToWebFirebase(data.email, data.password);
      return result;
    }

    return auth.signInWithEmailAndPassword(data.email, data.password);
  }

  function sendPasswordResetEmail(email: string) {
    if (isNative) {
      return FirebaseAuthentication.sendPasswordResetEmail({ email });
    }

    return auth.sendPasswordResetEmail(email);
  }

  function verifyPasswordResetCode(oobCode: string) {
    // if (isNative) {
    //   return new Promise((res, rej) => res(true));
    // }

    return auth.verifyPasswordResetCode(oobCode);
  }

  function confirmPasswordReset(data: {
    oobCode: string;
    newPassword: string;
  }) {
    if (isNative) {
      return FirebaseAuthentication.confirmPasswordReset(data);
    }

    return auth.confirmPasswordReset(data.oobCode, data.newPassword);
  }

  function checkActionCode(oobCode: string) {
    // if (isNative) {
    // }

    return auth.checkActionCode(oobCode);
  }

  function applyActionCode(oobCode: string) {
    return auth.applyActionCode(oobCode);
  }

  async function signInWithGoogle() {
    if (isNative) {
      const result = await FirebaseAuthentication.signInWithGoogle();
      await syncNativeSignInToWebFirebase(result);
      return result;
    }

    return auth.signInWithPopup(googleProvider);
  }

  async function signInWithApple() {
    if (isNative) {
      const result = await FirebaseAuthentication.signInWithApple();
      await syncNativeSignInToWebFirebase(result);
      return result;
    }

    return auth.signInWithPopup(appleProvider);
  }

  async function signInWithFacebook() {
    if (isNative) {
      const result = await FirebaseAuthentication.signInWithFacebook();
      await syncNativeSignInToWebFirebase(result);
      return result;
    }

    return auth.signInWithPopup(facebookProvider);
  }

  function signOut() {
    if (isNative) {
      return FirebaseAuthentication.signOut().finally(() => auth.signOut());
    }

    return auth.signOut();
  }

  async function updatePassword(newPassword: string) {
    if (isNative) {
      const currentUser = await getCurrentUser();

      if (!currentUser) {
        return;
      }

      return FirebaseAuthentication.updatePassword({
        newPassword,
      });
    }

    return auth.currentUser.updatePassword(newPassword);
  }

  /** Link email/password to current account. Web only; on native no-op. */
  async function linkWithEmailAndPassword(data: {
    email: string;
    password: string;
  }) {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    if (isNative) {
      throw { code: 'auth/linking-not-supported' };
    }
    const credential = firebase.auth.EmailAuthProvider.credential(
      data.email,
      data.password,
    );
    return user.linkWithCredential(credential);
  }

  /** Link provider (Google, Apple, Facebook) to current account. Web only. */
  async function linkWithPopup(provider: 'google' | 'apple' | 'facebook') {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    if (isNative) {
      throw { code: 'auth/linking-not-supported' };
    }
    const p =
      provider === 'google'
        ? googleProvider
        : provider === 'apple'
          ? appleProvider
          : facebookProvider;
    return user.linkWithPopup(p);
  }

  return {
    getIdToken,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    verifyPasswordResetCode,
    confirmPasswordReset,
    checkActionCode,
    applyActionCode,
    signInWithGoogle,
    signInWithApple,
    signInWithFacebook,
    signOut,
    updatePassword,
    getCurrentUser,
    linkWithEmailAndPassword,
    linkWithPopup,
  };
}
